import { IconNames } from '@dixa/lib-shared-habla';
import { useRef, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { Dictionary } from 'ts-essentials';

import { useAppPathParams } from 'utils/url/useAppPathParams';

const statusIcons = {
  none: undefined,
  success: 'check-circle-filled',
  error: 'exclamation-triangle-filled',
} as const satisfies Dictionary<IconNames | undefined>;

type Status = keyof typeof statusIcons;

const statusResetTimeout = 3000;

type UseSaveStatusResult = [
  icon: IconNames | undefined,
  setStatus: (status: Status) => void,
];

/**
 * Returns an icon based on a field's update api call result. The icon will
 * reset after some seconds.
 */
export function useSaveStatusIcon(): UseSaveStatusResult {
  const { csid } = useAppPathParams('/conversation/:csid');
  const statusTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [iconName, setIconName] = useState<IconNames>();

  const setStatus = (status: Status) => {
    setIconName(statusIcons[status]);

    if (statusTimeoutRef.current) {
      clearTimeout(statusTimeoutRef.current);
    }

    statusTimeoutRef.current = setTimeout(
      () => setStatus('none'),
      statusResetTimeout,
    );
  };

  // Handles resetting the icon when new conversation is loaded.
  useUpdateEffect(() => {
    if (statusTimeoutRef.current) {
      clearTimeout(statusTimeoutRef.current);
    }

    setIconName(statusIcons.none);
  }, [csid]);

  return [iconName, setStatus];
}
